import styled from 'styled-components';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { useState } from 'react';
import { useTheme, useCurrentUser, useIsInIframe } from 'hooks';
import { useSelector } from 'react-redux';
import { getPhotoSeries } from 'redux/root.selectors';
import { LandscapeLock } from 'components/containers';
import { Eco, ThumbsUp } from 'assets';
import { PhotoSeries, WindshieldRepairReplaceDecision as PhotoSeriesWindshieldRepairDecision } from 'redux/photoseries/photoseries';
import { ButtonContent, Button, RingLoader } from 'components/partials';
import DownloadImagesButton from './download-images-button.component';
import { useTranslate } from 'services';
import { useIsDarkTheme } from 'hooks/useTheme/useTheme.util';
import { RedirectToSigning, getRepairDecisionString } from './thank-you.component';

const { user_feedback, vehicle_scan } = TRANSLATIONS_VALUES_KEYS;

const Title = styled.h2<{paddingTop?: string}>`
  font-size: 1.75rem;
  padding-top: ${(props) => props.paddingTop || '7rem'};
`;

const InspectionSummary = styled.div`
  padding-top: 2.5rem;
  padding-left: 3.5rem;
  padding-right: 3.5rem;
`;

const InspectionSummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 3.125rem;

  h2 {
    margin-bottom: 0;
  }
`;

const RepairDecision = styled.div<{ borderColor: string, backgroundColor: string}>`
  margin-top: 1rem;
  border: 1px solid ${({ borderColor }) => borderColor};
  border-radius: 0.625rem;
  padding: 1rem;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const RepairDecisionHeader = styled.div<{ color: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h5 {
    font-size: 1.125rem;
    margin-bottom: 0;
    color: ${({ color }) => color};
  }

  margin-bottom: .5rem;
`;

const RepairDecisionDescription = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  text-align: left;
  font-size: 0.9rem;
  line-height: 1.4rem;
`;

const RepairDecisionActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;

  button {
    min-width: 45%;
  }
`;

const RingLoaderWrapper = styled.div`
  padding-top: 3rem;
`;

interface WindshieldRepairReplaceDecisionProps {
  onContinueClicked?: () => void;
  photoSeriesId: string;
  vin: string;
  repairDecision: PhotoSeriesWindshieldRepairDecision;
  customerName: string;
  showDownloadImagesButton: boolean;
}

type TerminalView = 'decision' | 'signing';

const WindshieldRepairDecision = (props: WindshieldRepairReplaceDecisionProps) => {
  const { styles } = useTheme();
  const {
    onContinueClicked, photoSeriesId, vin, customerName,
    showDownloadImagesButton, repairDecision
  } = props;
  const [isDownloading, setIsDownloading] = useState(false);
  const [terminalView, setTerminalView] = useState<TerminalView>('decision');
  const { translate, translateUnsafe } = useTranslate();
  const isDarkTheme = useIsDarkTheme();

  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);
  const currentUser = useCurrentUser();
  const returnUrl = currentUser?.returnUrl;
  const isInIframe = useIsInIframe();

  const repairDecisionString = getRepairDecisionString(repairDecision);

  const handleContinue = () => {
    if (photoSeries.reportSigningRequired) {
      setTerminalView('signing');
      return;
    }

    if (isInIframe) {
      window.parent.postMessage({ action: 'inspectionCompleted', windshieldRepairDecision: repairDecisionString }, '*');

      if (returnUrl && returnUrl.length > 0) {
        let modifiedReturnUrl = returnUrl;
        if (repairDecisionString && photoSeries.addWindshieldRepairDecisionToReturnUrl) {
          const decisionParam = `windshield=${repairDecisionString}`;
          modifiedReturnUrl += modifiedReturnUrl.includes('?') ? `&${decisionParam}` : `?${decisionParam}`;
        }
        window.location.replace(modifiedReturnUrl);
      }

      return;
    }

    onContinueClicked?.();
  };

  if (terminalView === 'signing') {
    return <RedirectToSigning photoSeriesId={photoSeriesId} />;
  }

  // Loading screen when analysis is onging
  if (repairDecision === PhotoSeriesWindshieldRepairDecision.Calculating) {
    return (
      <LandscapeLock>
        <InspectionSummary>
          <InspectionSummaryHeader>
            <Title data-testid="inspection-summary-title" paddingTop="0rem">
              { translate(user_feedback.inspection_summary) }
            </Title>
          </InspectionSummaryHeader>
          <RingLoaderWrapper className="mt-5">
            <RingLoader
              fontSize="0.5rem"
              color="white"
              bgColor={styles.colorPrimaryButtonBackground}
            />
          </RingLoaderWrapper>
          <div className="mt-5">
            { translate(user_feedback.windshield_inspection_summary_loading) }
          </div>
        </InspectionSummary>
      </LandscapeLock>
    );
  }

  // Defaults
  let settings = {
    title: '-',
    description: '-',
    ecoIconStrokeColor: styles.colorParagraph,
    ecoIconFillColor: 'white',
    repairDecisionColor: styles.colorParagraph,
    repairDecisionDescriptionBackgroundColor: 'white',
    // gray
    repairDecisionDescriptionBorderColor: '#D7DCE2'
  };

  // No action required
  if (repairDecision === PhotoSeriesWindshieldRepairDecision.NoActionRequired) {
    settings.title = user_feedback.windshield_inspection_summary_no_action;
    settings.description = user_feedback.windshield_inspection_summary_information_no_action;

    if (isDarkTheme) {
      settings.repairDecisionDescriptionBackgroundColor = styles.colorBackgroundRaised;
      settings.repairDecisionDescriptionBorderColor = styles.colorBackgroundRaised;
    }
  // Repair
  } else if (repairDecision === PhotoSeriesWindshieldRepairDecision.Repair) {
    settings.title = user_feedback.windshield_inspection_summary_repair;
    settings.description = user_feedback.windshield_inspection_summary_information_repair;
    settings.ecoIconStrokeColor = styles.colorGreenPrimary;
    settings.repairDecisionColor = styles.colorGreenPrimary;

    if (isDarkTheme) {
      settings.repairDecisionDescriptionBackgroundColor = styles.colorBackgroundRaised;
      settings.repairDecisionDescriptionBorderColor = styles.colorBackgroundRaised;
      settings.ecoIconFillColor = styles.colorBackgroundRaised;
    } else {
      settings.repairDecisionDescriptionBackgroundColor = styles.colorGreenLight;
      settings.repairDecisionDescriptionBorderColor = styles.colorGreenSecondary;
      settings.ecoIconFillColor = styles.colorGreenLight;
    }

  // Replace
  } else if (repairDecision === PhotoSeriesWindshieldRepairDecision.Replace) {
    settings.title = user_feedback.windshield_inspection_summary_replace;
    settings.description = user_feedback.windshield_inspection_summary_information_replace;

    if (isDarkTheme) {
      settings.repairDecisionDescriptionBackgroundColor = styles.colorBackgroundRaised;
      settings.repairDecisionDescriptionBorderColor = styles.colorBackgroundRaised;
    }
  // Uncertain
  } else if (repairDecision === PhotoSeriesWindshieldRepairDecision.Uncertain) {
    settings.title = user_feedback.windshield_inspection_summary_uncertain;
    settings.description = user_feedback.windshield_inspection_summary_information_uncertain;

    if (isDarkTheme) {
      settings.repairDecisionDescriptionBackgroundColor = styles.colorBackgroundRaised;
      settings.repairDecisionDescriptionBorderColor = styles.colorBackgroundRaised;
    }
  } else {
    throw new Error(`${repairDecision} Not implemented!`);
  }

  return (
    <LandscapeLock>
      <InspectionSummary>
        <InspectionSummaryHeader>
          <Title data-testid="inspection-summary-title" paddingTop="0rem">
            { translate(user_feedback.inspection_summary) }
          </Title>
        </InspectionSummaryHeader>
        <RepairDecision
          backgroundColor={settings.repairDecisionDescriptionBackgroundColor}
          borderColor={settings.repairDecisionDescriptionBorderColor}
        >
          <RepairDecisionHeader color={settings.repairDecisionColor}>
            <h5>
              { translate(settings.title) }
            </h5>
            {
              repairDecision === PhotoSeriesWindshieldRepairDecision.Repair && (
                <Eco
                  stroke={settings.ecoIconStrokeColor}
                  fill={settings.ecoIconFillColor}
                />
              )
            }
            {
              repairDecision === PhotoSeriesWindshieldRepairDecision.NoActionRequired && (
                <ThumbsUp fill={settings.repairDecisionColor} />
              )
            }
          </RepairDecisionHeader>
          <RepairDecisionDescription color={styles.colorParagraph}>
            { translateUnsafe(settings.description) }
          </RepairDecisionDescription>
        </RepairDecision>
        <RepairDecisionDescription className="mt-3" color={styles.colorParagraph}>
          { translate(vehicle_scan.seriesComplete_publicUser_text, { publicUserPhotoSeriesRecipient: customerName }) }
        </RepairDecisionDescription>
        <RepairDecisionActions>
          {
            showDownloadImagesButton && (
              <DownloadImagesButton
                photoSeriesId={photoSeriesId}
                onDownloadCompleted={() => setIsDownloading(false)}
                vin={vin}
                disabled={isDownloading}
                onDownloadClicked={() => setIsDownloading(true)}
              />
            )
          }
          <Button
            type="button"
            testId="btnNoThanks"
            onClick={handleContinue}
            bg={styles.colorSecondaryButtonBackground}
            color={styles.colorSecondaryButtonText}
            hoverBorderColor={styles.colorSecondaryButtonBackgroundDark}
            hoverShadowColor={styles.colorSecondaryButtonBackgroundLight}
          >
            <ButtonContent>
              { translate(vehicle_scan.continue_btn) }
            </ButtonContent>
          </Button>
        </RepairDecisionActions>
      </InspectionSummary>
    </LandscapeLock>
  );
};

export default WindshieldRepairDecision;
